import React, { useState } from 'react';
import { keyframes } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Typography, Button, Chip,
} from '@mui/material';
import ExploreIcon from '@mui/icons-material/Explore';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import MapIcon from '@mui/icons-material/Map';
import HubIcon from '@mui/icons-material/Hub';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SportsMmaIcon from '@mui/icons-material/SportsMma';
import CampaignIcon from '@mui/icons-material/Campaign';
import PeopleIcon from '@mui/icons-material/People';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ChatIcon from '@mui/icons-material/Chat';
import NotificationsIcon from '@mui/icons-material/Notifications';
import StarIcon from '@mui/icons-material/Star';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import LaptopIcon from '@mui/icons-material/Laptop';
import WatchIcon from '@mui/icons-material/Watch';
import { DEFAULT_THEME_COLOR, DEFAULT_GRADIENT, DEFAULT_GRADIENT_HOVER, DEFAULT_DARK_GRADIENT } from '../../constants/theme';
import { CONTEXT_CHANGE } from '../../constants/actionTypes';
import { store } from '../../store';
import { useLanguageContext } from '../../contexts/LanguageContext';
import aboutTranslations from './aboutTranslations';
import { THE_FREAKS_GUIDE_PATH } from '../TheFreaksGuide/TheFreaksGuideView';
import UsersCounterComponent from './UsersCounterComponent';
import AddToHomeScreenButton from '../AddToHomeScreen/AddToHomeScreenButton';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OfflineBoltIcon from '@mui/icons-material/OfflineBolt';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import HomeIcon from '@mui/icons-material/Home';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import HandshakeIcon from '@mui/icons-material/Handshake';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RouteIcon from '@mui/icons-material/Route';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const titleShine = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const AboutView = () => {
  const navigate = useNavigate();
  const { language, handleChangeLanguage } = useLanguageContext();
  const t = aboutTranslations[language] || aboutTranslations.en;

  // Latest news cards, newest first. Only the first 3 show until "show more".
  const [showAllNews, setShowAllNews] = useState(false);

  const newsIconCircleSx = {
    flexShrink: 0,
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: `${DEFAULT_THEME_COLOR}18`,
    border: `1px solid ${DEFAULT_THEME_COLOR}44`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '2rem',
  };
  const newsCardSx = (gradient) => ({
    width: '100%',
    maxWidth: 640,
    mx: 'auto',
    background: gradient,
    border: `1px solid ${DEFAULT_THEME_COLOR}33`,
    borderRadius: 3,
    p: { xs: 3, md: 4 },
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'flex-start', sm: 'center' },
    gap: { xs: 2, sm: 3 },
  });
  const newsBadgeChip = (
    <Chip
      label={t.news.badge}
      size="small"
      sx={{
        backgroundColor: `${DEFAULT_THEME_COLOR}22`,
        color: DEFAULT_THEME_COLOR,
        fontWeight: 700,
        fontSize: '0.7rem',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        border: `1px solid ${DEFAULT_THEME_COLOR}44`,
      }}
    />
  );
  const newsContextChip = (label) => (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', borderColor: 'rgba(255,255,255,0.2)' }}
    />
  );
  const renderSingleCtaNewsCard = ({ key, emoji, gradient, context, title, description, cta, onClick }) => (
    <Box key={key} sx={newsCardSx(gradient)}>
      <Box sx={newsIconCircleSx}>{emoji}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
          {newsBadgeChip}
          {newsContextChip(context)}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 2 }}>
          {description}
        </Typography>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={onClick}
          sx={{ background: DEFAULT_GRADIENT, color: 'white', '&:hover': { background: DEFAULT_GRADIENT_HOVER } }}
        >
          {cta}
        </Button>
      </Box>
    </Box>
  );

  const newsCards = [
    renderSingleCtaNewsCard({
      key: 'theFreaks',
      emoji: '🥊',
      gradient: DEFAULT_DARK_GRADIENT,
      context: t.news.theFreaks.context,
      title: t.news.theFreaks.title,
      description: t.news.theFreaks.description,
      cta: t.news.theFreaks.cta,
      // /about skips the auth bootstrap in App, so a logged-out visitor has no
      // token here — show them the public guide with register / login instead.
      onClick: () => navigate(store.getState().common?.token ? '/the-freaks' : THE_FREAKS_GUIDE_PATH),
    }),
    renderSingleCtaNewsCard({
      key: 'fishingRadar',
      emoji: '🎣',
      gradient: 'linear-gradient(135deg, #0d1b2a 0%, #0d2233 100%)',
      context: t.news.fishingRadar.context,
      title: t.news.fishingRadar.title,
      description: t.news.fishingRadar.description,
      cta: t.news.fishingRadar.cta,
      onClick: () => {
        store.dispatch({ type: CONTEXT_CHANGE, payload: 'fishing' });
        navigate('/places?fishingRadar=open');
      },
    }),
    renderSingleCtaNewsCard({
      key: 'mushroomRadar',
      emoji: '🍄',
      gradient: 'linear-gradient(135deg, #0d1b2a 0%, #0d2a1e 100%)',
      context: t.news.mushroomRadar.context,
      title: t.news.mushroomRadar.title,
      description: t.news.mushroomRadar.description,
      cta: t.news.mushroomRadar.cta,
      onClick: () => {
        store.dispatch({ type: CONTEXT_CHANGE, payload: 'mushroom' });
        navigate('/places?mushroomRadar=open');
      },
    }),
    (
      <Box key="fishing" sx={newsCardSx('linear-gradient(135deg, #0d1b2a 0%, #0d2233 100%)')}>
        <Box sx={newsIconCircleSx}>🎣</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
            {newsBadgeChip}
            {newsContextChip(t.news.fishing.context)}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
            {t.news.fishing.title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 2 }}>
            {t.news.fishing.description}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {[
              { tab: 'prediction', label: t.news.fishing.prediction },
              { tab: 'catches', label: t.news.fishing.catches },
              { tab: 'weather', label: t.news.fishing.weather },
            ].map(({ tab, label }) => (
              <Button
                key={tab}
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={() => {
                  store.dispatch({ type: CONTEXT_CHANGE, payload: 'fishing' });
                  navigate(`/places-restricted?fishing=${tab}`);
                }}
                sx={{ background: DEFAULT_GRADIENT, color: 'white', '&:hover': { background: DEFAULT_GRADIENT_HOVER } }}
              >
                {label}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>
    ),
  ];
  const visibleNewsCards = showAllNews ? newsCards : newsCards.slice(0, 3);

  const featureCards = [
    { icon: <ExploreIcon sx={{ fontSize: 40, color: DEFAULT_THEME_COLOR }} />, title: t.features.freaks.title, desc: t.features.freaks.description },
    { icon: <TravelExploreIcon sx={{ fontSize: 40, color: DEFAULT_THEME_COLOR }} />, title: t.features.places.title, desc: t.features.places.description },
    { icon: <HubIcon sx={{ fontSize: 40, color: DEFAULT_THEME_COLOR }} />, title: t.features.hub.title, desc: t.features.hub.description },
    { icon: <GpsFixedIcon sx={{ fontSize: 40, color: DEFAULT_THEME_COLOR }} />, title: t.features.tracker.title, desc: t.features.tracker.description },
    { icon: <EmojiEventsIcon sx={{ fontSize: 40, color: DEFAULT_THEME_COLOR }} />, title: t.features.shake.title, desc: t.features.shake.description },
    { icon: <CampaignIcon sx={{ fontSize: 40, color: DEFAULT_THEME_COLOR }} />, title: t.features.campaigns.title, desc: t.features.campaigns.description },
    { icon: <SportsMmaIcon sx={{ fontSize: 40, color: DEFAULT_THEME_COLOR }} />, title: t.features.theFreaks.title, desc: t.features.theFreaks.description },
  ];

  const socialItems = [
    { icon: <PeopleIcon />, text: t.social.follow },
    { icon: <FavoriteIcon />, text: t.social.react },
    { icon: <ChatIcon />, text: t.social.chat },
    { icon: <NotificationsIcon />, text: t.social.notifications },
    { icon: <StarIcon />, text: t.social.leaderboard },
  ];

  const platformItems = [
    { icon: <LaptopIcon sx={{ fontSize: 48, color: DEFAULT_THEME_COLOR }} />, label: t.platforms.web },
    { icon: <PhoneIphoneIcon sx={{ fontSize: 48, color: DEFAULT_THEME_COLOR }} />, label: t.platforms.mobile, accessSubject: 'iOS & Android Access Request' },
    { icon: <WatchIcon sx={{ fontSize: 48, color: DEFAULT_THEME_COLOR }} />, label: t.platforms.watch, note: t.platforms.watchNote, accessSubject: 'Apple Watch Access Request' },
  ];

  return (
    <Box>
      {/* ── Hero ── */}
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0d1b2a 0%, #0d3d2e 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          px: 3,
          textAlign: 'center',
        }}
      >
        {/* Language toggle */}
        <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 1 }}>
          {['en', 'pl'].map((lang) => (
            <Button
              key={lang}
              size="small"
              variant={language === lang ? 'contained' : 'outlined'}
              onClick={() => handleChangeLanguage(lang)}
              sx={{
                minWidth: 44,
                color: language === lang ? 'white' : DEFAULT_THEME_COLOR,
                backgroundColor: language === lang ? DEFAULT_THEME_COLOR : 'transparent',
                borderColor: DEFAULT_THEME_COLOR,
                '&:hover': { backgroundColor: DEFAULT_THEME_COLOR, color: 'white' },
              }}
            >
              {lang.toUpperCase()}
            </Button>
          ))}
        </Box>

        <Typography
          variant="h1"
          sx={{
            background: 'linear-gradient(90deg, #36c9a2 0%, #4facfe 35%, rgba(255,255,255,0.9) 50%, #4facfe 65%, #36c9a2 100%)',
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 800,
            fontSize: { xs: '3rem', md: '4.5rem' },
            letterSpacing: '-0.02em',
            mb: 4,
            animation: `${titleShine} 4s linear infinite`,
          }}
        >
          Freakland
        </Typography>

        <Typography
          variant="h2"
          sx={{
            color: 'white',
            fontWeight: 700,
            mb: 2,
            fontSize: { xs: '2rem', md: '3rem' },
          }}
        >
          {t.hero.headline}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            color: 'rgba(255,255,255,0.75)',
            maxWidth: 600,
            mb: 5,
            fontWeight: 400,
          }}
        >
          {t.hero.subline}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            size="large"
            variant="contained"
            onClick={() => navigate('/register')}
            sx={{
              background: DEFAULT_GRADIENT,
              color: 'white',
              px: 4,
              '&:hover': { background: DEFAULT_GRADIENT_HOVER },
            }}
          >
            {t.hero.register}
          </Button>
          <Button
            size="large"
            variant="outlined"
            onClick={() => navigate('/login')}
            sx={{
              borderColor: DEFAULT_THEME_COLOR,
              color: DEFAULT_THEME_COLOR,
              px: 4,
              '&:hover': { backgroundColor: 'rgba(54,201,162,0.1)' },
            }}
          >
            {t.hero.login}
          </Button>
        </Box>
      </Box>

      {/* ── Latest News ── */}
      <Box sx={{ py: { xs: 6, md: 8 }, px: { xs: 3, md: 8 }, background: 'linear-gradient(135deg, #091420 0%, #0d1b2a 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: { xs: 4, md: 5 } }}>
          <NewReleasesIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 32 }} />
          <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.5rem' }, color: 'white' }}>
            {t.news.heading}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          {visibleNewsCards}
        </Box>
        {newsCards.length > 3 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              endIcon={<ExpandMoreIcon sx={{ transform: showAllNews ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />}
              onClick={() => setShowAllNews((prev) => !prev)}
              sx={{
                borderColor: `${DEFAULT_THEME_COLOR}66`,
                color: DEFAULT_THEME_COLOR,
                '&:hover': { borderColor: DEFAULT_THEME_COLOR, backgroundColor: 'rgba(54,201,162,0.1)' },
              }}
            >
              {showAllNews ? t.news.showLess : t.news.showMore}
            </Button>
          </Box>
        )}
      </Box>

      {/* ── Feature Cards ── */}
      <Box sx={{ py: 10, px: { xs: 3, md: 8 }, background: 'linear-gradient(135deg, #0d1b2a 0%, #091420 100%)' }}>
        <Typography
          variant="h3"
          sx={{ textAlign: 'center', fontWeight: 700, mb: 6, fontSize: { xs: '1.75rem', md: '2.5rem' }, color: 'white' }}
        >
          {t.features.heading}
        </Typography>
        <Grid container spacing={3} justifyContent="center">
          {featureCards.map((card) => (
            <Grid item xs={12} sm={6} md={4} key={card.title}>
              <Box
                sx={{
                  height: '100%',
                  background: 'linear-gradient(135deg, #0d1b2a 0%, #0d2a1e 100%)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 3,
                  p: 4,
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1.5,
                  transition: 'box-shadow 0.2s, border-color 0.2s',
                  '&:hover': {
                    boxShadow: '0 8px 32px rgba(54,201,162,0.2)',
                    borderColor: 'rgba(54,201,162,0.35)',
                  },
                }}
              >
                {card.icon}
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                  {card.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                  {card.desc}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── Early Access: GPS Track Recording ── */}
      <Box sx={{ py: 10, px: { xs: 3, md: 8 }, background: 'linear-gradient(135deg, #0d1b2a 0%, #0d2216 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 1 }}>
          <RouteIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 32 }} />
          <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.5rem' }, color: 'white' }}>
            {t.earlyAccess.heading}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Chip
            label={t.earlyAccess.chip}
            icon={<LockOpenIcon sx={{ fontSize: '14px !important', color: `${DEFAULT_THEME_COLOR} !important` }} />}
            sx={{
              backgroundColor: `${DEFAULT_THEME_COLOR}18`,
              color: DEFAULT_THEME_COLOR,
              fontWeight: 700,
              fontSize: '0.78rem',
              border: `1px solid ${DEFAULT_THEME_COLOR}44`,
              letterSpacing: '0.04em',
            }}
          />
        </Box>
        <Typography variant="body1" sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', mb: 6, maxWidth: 560, mx: 'auto' }}>
          {t.earlyAccess.subline}
        </Typography>

        <Box
          sx={{
            maxWidth: 480,
            mx: 'auto',
            background: 'rgba(255,255,255,0.03)',
            border: `1px solid ${DEFAULT_THEME_COLOR}33`,
            borderRadius: 3,
            p: { xs: 3, md: 4 },
          }}
        >
          <Typography sx={{ color: 'white', fontWeight: 700, mb: 2.5, fontSize: '1rem' }}>
            {t.earlyAccess.howTitle}
          </Typography>
          {[t.earlyAccess.step1, t.earlyAccess.step2, t.earlyAccess.step3].map((step, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.9 }}>
              <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: `${DEFAULT_THEME_COLOR}20`, border: `1px solid ${DEFAULT_THEME_COLOR}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Typography sx={{ color: DEFAULT_THEME_COLOR, fontSize: '0.7rem', fontWeight: 700 }}>{i + 1}</Typography>
              </Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.875rem' }}>{step}</Typography>
            </Box>
          ))}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<RouteIcon />}
              component="a"
              href={`mailto:freakland.app@gmail.com?subject=${encodeURIComponent('Track Access Request')}`}
              sx={{ background: DEFAULT_GRADIENT, color: 'white', fontWeight: 700, borderRadius: 2, px: 3, '&:hover': { background: DEFAULT_GRADIENT_HOVER } }}
            >
              {t.earlyAccess.cta}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── Blasts ── */}
      <Box sx={{ py: 10, px: { xs: 3, md: 8 }, background: 'linear-gradient(135deg, #0d1b2a 0%, #1a0d0d 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 2 }}>
          <LocalFireDepartmentIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 32 }} />
          <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.5rem' }, color: 'white' }}>
            {t.blasts.heading}
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', mb: 7, maxWidth: 560, mx: 'auto' }}>
          {t.blasts.subline}
        </Typography>

        <Grid container spacing={3} justifyContent="center" sx={{ mb: 6 }}>
          {[
            { title: t.blasts.feature1Title, desc: t.blasts.feature1Desc, emoji: '📱' },
            { title: t.blasts.feature2Title, desc: t.blasts.feature2Desc, emoji: '🔥' },
            { title: t.blasts.feature3Title, desc: t.blasts.feature3Desc, emoji: '📍' },
          ].map(({ title, desc, emoji }) => (
            <Grid item xs={12} sm={4} key={title}>
              <Box sx={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${DEFAULT_THEME_COLOR}22`, borderRadius: 3, p: 3, height: '100%', textAlign: 'center', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: `0 0 24px ${DEFAULT_THEME_COLOR}22` } }}>
                <Typography sx={{ fontSize: '2.2rem', mb: 1.5 }}>{emoji}</Typography>
                <Typography sx={{ color: 'white', fontWeight: 700, mb: 1 }}>{title}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem', lineHeight: 1.6 }}>{desc}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<LocalFireDepartmentIcon />}
            onClick={() => navigate('/blasts')}
            sx={{ background: DEFAULT_GRADIENT, color: 'white', fontWeight: 700, px: 4, borderRadius: 3, '&:hover': { background: DEFAULT_GRADIENT_HOVER } }}
          >
            {t.blasts.cta}
          </Button>
        </Box>
      </Box>

      {/* ── Explore Without Account ── */}
      <Box
        sx={{
          py: 10,
          px: { xs: 3, md: 8 },
          background: 'linear-gradient(135deg, #0d1b2a 0%, #0d3d2e 100%)',
        }}
      >
        <Typography
          variant="h3"
          sx={{ textAlign: 'center', fontWeight: 700, mb: 2, fontSize: { xs: '1.75rem', md: '2.5rem' }, color: 'white' }}
        >
          {t.explore.heading}
        </Typography>
        <Typography
          variant="body1"
          sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.65)', mb: 6, maxWidth: 560, mx: 'auto' }}
        >
          {t.explore.subline}
        </Typography>

        <Grid container spacing={4} justifyContent="center">
          {/* Freaks card */}
          <Grid item xs={12} sm={6} md={5}>
            <Box
              sx={{
                background: 'linear-gradient(135deg, #0d1b2a 0%, #0d2a1e 100%)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 3,
                p: 4,
                textAlign: 'center',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: `0 8px 32px rgba(54,201,162,0.2)`, borderColor: `${DEFAULT_THEME_COLOR}44` },
              }}
            >
              <ExploreIcon sx={{ fontSize: 52, color: DEFAULT_THEME_COLOR }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                {t.explore.freaks.title}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)', flex: 1 }}>
                {t.explore.freaks.description}
              </Typography>
              <Button
                size="large"
                variant="contained"
                onClick={() => navigate('/freaks-restricted')}
                sx={{
                  background: DEFAULT_GRADIENT,
                  color: 'white',
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 4,
                  mt: 1,
                  '&:hover': { background: DEFAULT_GRADIENT_HOVER },
                }}
              >
                {t.explore.freaks.button}
              </Button>
            </Box>
          </Grid>

          {/* Places card */}
          <Grid item xs={12} sm={6} md={5}>
            <Box
              sx={{
                background: 'linear-gradient(135deg, #0d1b2a 0%, #0d2a1e 100%)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 3,
                p: 4,
                textAlign: 'center',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: `0 8px 32px rgba(79,172,254,0.2)`, borderColor: `#4facfe44` },
              }}
            >
              <MapIcon sx={{ fontSize: 52, color: '#4facfe' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                {t.explore.places.title}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)', flex: 1 }}>
                {t.explore.places.description}
              </Typography>
              <Button
                size="large"
                variant="outlined"
                onClick={() => navigate('/places-restricted')}
                sx={{
                  borderColor: '#4facfe',
                  color: '#4facfe',
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 4,
                  mt: 1,
                  '&:hover': { backgroundColor: 'rgba(79,172,254,0.1)', borderColor: '#4facfe' },
                }}
              >
                {t.explore.places.button}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* ── Activity Contexts ── */}
      <Box sx={{ py: 10, px: { xs: 3, md: 8 }, backgroundColor: '#f9f9f9' }}>
        <Typography
          variant="h3"
          sx={{ textAlign: 'center', fontWeight: 700, mb: 2, fontSize: { xs: '1.75rem', md: '2.5rem' }, color: '#1a1a1a' }}
        >
          {t.contexts.heading}
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'center', color: '#666', mb: 5 }}>
          {t.contexts.subline}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            justifyContent: 'center',
            maxWidth: 900,
            mx: 'auto',
          }}
        >
          {t.contexts.list.map((ctx) => (
            <Chip
              key={ctx}
              label={ctx}
              sx={{
                backgroundColor: `${DEFAULT_THEME_COLOR}22`,
                color: DEFAULT_THEME_COLOR,
                fontWeight: 600,
                fontSize: '0.85rem',
                border: `1px solid ${DEFAULT_THEME_COLOR}44`,
              }}
            />
          ))}
        </Box>
      </Box>

      {/* ── Social Features ── */}
      <Box sx={{ py: 10, px: { xs: 3, md: 8 }, backgroundColor: 'white' }}>
        <Typography
          variant="h3"
          sx={{ textAlign: 'center', fontWeight: 700, mb: 6, fontSize: { xs: '1.75rem', md: '2.5rem' }, color: '#1a1a1a' }}
        >
          {t.social.heading}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 4 }}>
          {socialItems.map((item) => (
            <Box key={item.text} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ color: DEFAULT_THEME_COLOR }}>{item.icon}</Box>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                {item.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Multi-Platform Banner ── */}
      <Box sx={{ py: 10, px: { xs: 3, md: 8 }, background: 'linear-gradient(135deg, #0d1b2a 0%, #091420 100%)' }}>
        <Typography
          variant="h3"
          sx={{ textAlign: 'center', fontWeight: 700, mb: 6, fontSize: { xs: '1.75rem', md: '2.5rem' }, color: 'white' }}
        >
          {t.platforms.heading}
        </Typography>
        <Grid container spacing={3} justifyContent="center">
          {platformItems.map((p) => (
            <Grid item xs={12} sm={4} key={p.label}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1.5,
                  background: 'linear-gradient(135deg, #0d1b2a 0%, #0d2a1e 100%)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 3,
                  px: 5,
                  py: 4,
                  height: '100%',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: '0 0 24px rgba(54,201,162,0.18)' },
                }}
              >
                {p.icon}
                <Typography variant="body1" sx={{ fontWeight: 700, color: 'white' }}>
                  {p.label}
                </Typography>
                {p.note && (
                  <Typography variant="caption" sx={{ color: DEFAULT_THEME_COLOR, fontWeight: 600 }}>
                    {p.note}
                  </Typography>
                )}
                {p.accessSubject && (
                  <Button
                    variant="contained"
                    size="small"
                    component="a"
                    href={`mailto:freakland.app@gmail.com?subject=${encodeURIComponent(p.accessSubject)}`}
                    sx={{ mt: 1, background: DEFAULT_GRADIENT, color: 'white', fontWeight: 700, borderRadius: 2, px: 2.5, '&:hover': { background: DEFAULT_GRADIENT_HOVER } }}
                  >
                    {t.platforms.requestAccess}
                  </Button>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── Add to Home Screen ── */}
      <Box sx={{ py: 10, px: { xs: 3, md: 8 }, background: 'linear-gradient(135deg, #0d1b2a 0%, #0d3d2e 100%)' }}>
        <Typography variant="h3" sx={{ textAlign: 'center', fontWeight: 700, mb: 2, fontSize: { xs: '1.75rem', md: '2.5rem' }, color: 'white' }}>
          {t.install.heading}
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', mb: 6, maxWidth: 560, mx: 'auto' }}>
          {t.install.subline}
        </Typography>

        {/* Benefits row */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: { xs: 2, md: 4 }, mb: 7 }}>
          {[
            { icon: <HomeIcon sx={{ fontSize: 22, color: DEFAULT_THEME_COLOR }} />, text: t.install.benefit1 },
            { icon: <FullscreenIcon sx={{ fontSize: 22, color: DEFAULT_THEME_COLOR }} />, text: t.install.benefit2 },
            { icon: <OfflineBoltIcon sx={{ fontSize: 22, color: DEFAULT_THEME_COLOR }} />, text: t.install.benefit3 },
            { icon: <NotificationsActiveIcon sx={{ fontSize: 22, color: DEFAULT_THEME_COLOR }} />, text: t.install.benefit4 },
          ].map(({ icon, text }) => (
            <Box key={text} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, maxWidth: 240 }}>
              <Box sx={{ flexShrink: 0 }}>{icon}</Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem', fontWeight: 500 }}>{text}</Typography>
            </Box>
          ))}
        </Box>

        {/* Platform instructions */}
        <Grid container spacing={3} justifyContent="center" sx={{ mb: 5 }}>
          {/* iOS */}
          <Grid item xs={12} sm={5}>
            <Box sx={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3, p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <PhoneIphoneIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 28 }} />
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>{t.install.iosHeading}</Typography>
              </Box>
              {[
                { icon: <IosShareIcon sx={{ fontSize: 18, color: DEFAULT_THEME_COLOR }} />, text: t.install.iosStep1 },
                { icon: <IosShareIcon sx={{ fontSize: 18, color: DEFAULT_THEME_COLOR }} />, text: t.install.iosStep2 },
                { icon: <AddBoxOutlinedIcon sx={{ fontSize: 18, color: DEFAULT_THEME_COLOR }} />, text: t.install.iosStep3 },
              ].map(({ icon, text }, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.9 }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: `${DEFAULT_THEME_COLOR}20`, border: `1px solid ${DEFAULT_THEME_COLOR}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Typography sx={{ color: DEFAULT_THEME_COLOR, fontSize: '0.7rem', fontWeight: 700 }}>{i + 1}</Typography>
                  </Box>
                  <Box sx={{ flexShrink: 0 }}>{icon}</Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>{text}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Android */}
          <Grid item xs={12} sm={5}>
            <Box sx={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3, p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <PhoneAndroidIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 28 }} />
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>{t.install.androidHeading}</Typography>
              </Box>
              {[
                { icon: <PhoneAndroidIcon sx={{ fontSize: 18, color: DEFAULT_THEME_COLOR }} />, text: t.install.androidStep1 },
                { icon: <MoreVertIcon sx={{ fontSize: 18, color: DEFAULT_THEME_COLOR }} />, text: t.install.androidStep2 },
                { icon: <AddBoxOutlinedIcon sx={{ fontSize: 18, color: DEFAULT_THEME_COLOR }} />, text: t.install.androidStep3 },
              ].map(({ icon, text }, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.9 }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: `${DEFAULT_THEME_COLOR}20`, border: `1px solid ${DEFAULT_THEME_COLOR}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Typography sx={{ color: DEFAULT_THEME_COLOR, fontSize: '0.7rem', fontWeight: 700 }}>{i + 1}</Typography>
                  </Box>
                  <Box sx={{ flexShrink: 0 }}>{icon}</Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>{text}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* CTA button */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <AddToHomeScreenButton label={t.install.cta} size="large" />
        </Box>
      </Box>

      {/* ── Partnership ── */}
      <Box sx={{ py: 10, px: { xs: 3, md: 8 }, background: 'linear-gradient(135deg, #091420 0%, #0d1b2a 100%)' }}>
        <Typography variant="h3" sx={{ textAlign: 'center', fontWeight: 700, mb: 2, fontSize: { xs: '1.75rem', md: '2.5rem' }, color: 'white' }}>
          {t.partnership.heading}
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', mb: 7, maxWidth: 580, mx: 'auto' }}>
          {t.partnership.subline}
        </Typography>

        <Grid container spacing={4} justifyContent="center">
          {/* Ambassador */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                background: 'linear-gradient(135deg, #0d2a1e 0%, #0d1b2a 100%)',
                border: `1px solid ${DEFAULT_THEME_COLOR}33`,
                borderRadius: 4,
                p: 4,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0, left: 0, right: 0, height: 3,
                  background: DEFAULT_GRADIENT,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 52, height: 52, borderRadius: '50%',
                    background: `${DEFAULT_THEME_COLOR}20`,
                    border: `2px solid ${DEFAULT_THEME_COLOR}44`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}
                >
                  <WorkspacePremiumIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 28 }} />
                </Box>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                  {t.partnership.ambassador.title}
                </Typography>
              </Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', lineHeight: 1.65 }}>
                {t.partnership.ambassador.description}
              </Typography>
              <Box sx={{ flex: 1 }}>
                {t.partnership.ambassador.perks.map((perk) => (
                  <Box key={perk} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.65 }}>
                    <CheckCircleOutlineIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 18, flexShrink: 0 }} />
                    <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem' }}>{perk}</Typography>
                  </Box>
                ))}
              </Box>
              <Box
                sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 1.25,
                  background: 'rgba(255,200,50,0.07)',
                  border: '1px solid rgba(255,200,50,0.22)',
                  borderRadius: 2.5,
                  px: 2, py: 1.5,
                }}
              >
                <EmojiEventsIcon sx={{ color: 'gold', fontSize: 18, mt: '2px', flexShrink: 0 }} />
                <Typography sx={{ color: 'rgba(255,200,50,0.85)', fontSize: '0.82rem', lineHeight: 1.55 }}>
                  {t.partnership.ambassador.giftsNote}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="large"
                component="a"
                href={`mailto:freakland.app@gmail.com?subject=${encodeURIComponent('Ambassador Application')}`}
                sx={{ background: DEFAULT_GRADIENT, color: 'white', fontWeight: 700, mt: 1, '&:hover': { background: DEFAULT_GRADIENT_HOVER } }}
              >
                {t.partnership.ambassador.cta}
              </Button>
            </Box>
          </Grid>

          {/* Brand */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                background: 'linear-gradient(135deg, #0d1b2a 0%, #12162e 100%)',
                border: '1px solid rgba(79,172,254,0.22)',
                borderRadius: 4,
                p: 4,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0, left: 0, right: 0, height: 3,
                  background: 'linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 52, height: 52, borderRadius: '50%',
                    background: 'rgba(79,172,254,0.15)',
                    border: '2px solid rgba(79,172,254,0.35)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}
                >
                  <HandshakeIcon sx={{ color: '#4facfe', fontSize: 28 }} />
                </Box>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                  {t.partnership.brand.title}
                </Typography>
              </Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', lineHeight: 1.65 }}>
                {t.partnership.brand.description}
              </Typography>
              <Box sx={{ flex: 1 }}>
                {t.partnership.brand.perks.map((perk) => (
                  <Box key={perk} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.65 }}>
                    <CheckCircleOutlineIcon sx={{ color: '#4facfe', fontSize: 18, flexShrink: 0 }} />
                    <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem' }}>{perk}</Typography>
                  </Box>
                ))}
              </Box>
              <Button
                variant="outlined"
                size="large"
                component="a"
                href={`mailto:freakland.app@gmail.com?subject=${encodeURIComponent('Brand Partnership Inquiry')}`}
                sx={{ borderColor: '#4facfe', color: '#4facfe', fontWeight: 700, mt: 1, '&:hover': { backgroundColor: 'rgba(79,172,254,0.1)', borderColor: '#4facfe' } }}
              >
                {t.partnership.brand.cta}
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Typography sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.35)', mt: 5, fontSize: '0.875rem' }}>
          {t.partnership.contactPrompt}{' '}
          <Box
            component="a"
            href="mailto:freakland.app@gmail.com"
            sx={{ color: DEFAULT_THEME_COLOR, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            freakland.app@gmail.com
          </Box>
        </Typography>
        <Typography sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.35)', mt: 1.5, fontSize: '0.875rem' }}>
          {t.partnership.feedbackPrompt}{' '}
          <Box
            component="span"
            onClick={() => navigate('/feedback')}
            sx={{ color: DEFAULT_THEME_COLOR, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            {t.partnership.feedbackLink}
          </Box>
        </Typography>
      </Box>

      {/* ── Registered Users Counter ── */}
      <UsersCounterComponent translation={t} />

      {/* ── Buy Me a Coffee ── */}
      <Box sx={{ py: 10, px: { xs: 3, md: 8 }, background: 'linear-gradient(135deg, #0d1b2a 0%, #2a1d0d 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 2 }}>
          <LocalCafeIcon sx={{ color: '#FFDD00', fontSize: 32 }} />
          <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.5rem' }, color: 'white' }}>
            {t.support.heading}
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', mb: 5, maxWidth: 560, mx: 'auto' }}>
          {t.support.subline}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<LocalCafeIcon />}
            component="a"
            href="https://buymeacoffee.com/pejaneczekr"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              backgroundColor: '#FFDD00',
              color: '#1a1a1a',
              fontWeight: 700,
              px: 4,
              py: 1.5,
              borderRadius: 3,
              '&:hover': { backgroundColor: '#e6c700' },
            }}
          >
            {t.support.cta}
          </Button>
        </Box>
      </Box>

      {/* ── Footer CTA ── */}
      <Box
        sx={{
          py: 12,
          px: 3,
          background: 'linear-gradient(135deg, #0d1b2a 0%, #0d3d2e 100%)',
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h3"
          sx={{ color: 'white', fontWeight: 700, mb: 4, fontSize: { xs: '1.75rem', md: '2.5rem' } }}
        >
          {t.cta.heading}
        </Typography>
        <Button
          size="large"
          variant="contained"
          onClick={() => navigate('/register')}
          sx={{
            backgroundColor: DEFAULT_THEME_COLOR,
            color: 'white',
            px: 6,
            py: 1.5,
            fontSize: '1.1rem',
            '&:hover': { backgroundColor: '#2aad8a' },
          }}
        >
          {t.cta.register}
        </Button>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mt: 3 }}>
          {t.cta.loginPrompt}{' '}
          <Box
            component="span"
            onClick={() => navigate('/login')}
            sx={{
              color: DEFAULT_THEME_COLOR,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {t.cta.login}
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

export default AboutView;
